/**
 * `entities/taxonomy` query options factories.
 *
 * The taxonomy endpoints aren't in the generated assets-api SDK yet, so these
 * call the hey-api client directly by URL (same convention as
 * `entities/project-member`'s role queries). `assetsHttp` auto-injects the
 * `x-selected-workspace-id` header, so workspace context is implicit.
 *
 * URL prefix `/api/v1` matches the assets-api URI versioning
 * (`setupApiVersioning`: prefix `api/v`, default version `1`).
 */
import { queryOptions } from '@tanstack/react-query';
import { assetsHttp } from '@/lib/api/http-client';
import type { Taxonomy, TaxonomyChangeLogEntry, TaxonomySummary } from '../model/types';
import { TAXONOMY_QUERY_KEYS } from './query-keys';

const TAXONOMIES_URL = '/api/v1/taxonomies';

export const taxonomiesQueryOptions = () =>
  queryOptions({
    queryKey: TAXONOMY_QUERY_KEYS.list(),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({ throwOnError: true, url: TAXONOMIES_URL, signal });
      return (response.data as TaxonomySummary[] | undefined) ?? [];
    },
  });

export const taxonomyDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: TAXONOMY_QUERY_KEYS.detail(id),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${TAXONOMIES_URL}/{id}`,
        path: { id },
        signal,
      });
      if (!response.data) {
        throw new Error('Failed to load taxonomy');
      }
      return response.data as Taxonomy;
    },
  });

export const taxonomyChangeLogQueryOptions = (id: string) =>
  queryOptions({
    queryKey: TAXONOMY_QUERY_KEYS.changeLog(id),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${TAXONOMIES_URL}/{id}/change-log`,
        path: { id },
        signal,
      });
      return (response.data as TaxonomyChangeLogEntry[] | undefined) ?? [];
    },
  });
